// Turning raw API values into words people read. Pages use these instead of
// showing slugs, raw phone strings, "undefined" or ISO dates.

const BLANK = new Set(["", "undefined", "null", "none", "nan"]);

// True for values the API uses to mean "nothing".
export const isBlank = (value) => value == null || BLANK.has(String(value).trim().toLowerCase());

// The quiet dash shown for a missing value.
export const EMPTY = "—";

// "super-admin" -> "Super admin", "read_delete" -> "Read delete", "test " -> "Test".
export function humanize(value) {
  if (isBlank(value)) return EMPTY;
  const words = String(value)
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Phone numbers as people write them: "+961 71 999 333", "79 303 698".
// Anything that is not a phone number is shown as given (or a dash).
export function formatPhone(value) {
  if (isBlank(value)) return EMPTY;
  const raw = String(value).trim();
  const digits = raw.replace(/[^\d]/g, "");
  if (digits.length < 6 || digits.length > 15) return raw;
  const plus = raw.startsWith("+") || raw.startsWith("00");
  let rest = raw.startsWith("00") ? digits.slice(2) : digits;
  let country = "";
  if (plus) {
    // Lebanon (+961) and common 1–3 digit codes: keep the code apart.
    const code = ["961", "971", "966", "974", "965", "33", "44", "49", "39", "1"].find((c) => rest.startsWith(c));
    if (code) {
      country = `+${code} `;
      rest = rest.slice(code.length);
    } else {
      country = "+";
    }
  }
  const groups =
    rest.length <= 7
      ? [rest.slice(0, 3), rest.slice(3)]
      : rest.length === 8
      ? [rest.slice(0, 2), rest.slice(2, 5), rest.slice(5)]
      : [rest.slice(0, 3), rest.slice(3, 6), rest.slice(6)];
  return country + groups.filter(Boolean).join(" ");
}

export const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const toDate = (value) => {
  if (isBlank(value)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// "20 Oct 2026"
export function formatDate(value) {
  const d = toDate(value);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : EMPTY;
}

// "14:05"
export function formatTime(value) {
  const d = toDate(value);
  return d ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : EMPTY;
}

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// "just now", "5 minutes ago", "3 hours ago", "yesterday", "4 days ago",
// "20 Oct 2026"; `never` when there is no date ("Never" by default).
export function relativeTime(value, { never = "Never", now = new Date() } = {}) {
  const d = toDate(value);
  if (!d) return never;
  const seconds = Math.round((now - d) / 1000);
  if (seconds < 0) return formatDate(d);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (days === 0) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDate(d);
}

// A day heading for timelines: "Today", "Yesterday", "Monday 19 Oct", or
// "19 Oct 2025" for another year.
export function dayHeading(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return EMPTY;
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (d.getFullYear() !== now.getFullYear()) return formatDate(d);
  if (days > 1 && days < 7) return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

// The calendar day of a date, for grouping ("2026-10-21").
export function dayKey(value) {
  const d = toDate(value);
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
}

// "1 item", "3 items"
export const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
